// ─────────────────────────────────────────────────────────────────────────
// MomoPaymentDialog
//
// Drives the Zenith GlobalPay mobile-money flow end to end, alongside (not
// instead of) the existing manual mobile-money path in PaymentDialog.js.
//
//   1. `form`    — network + phone. Prefilled from the patient record.
//   2. `waiting` — POST /paymentsMomo/initiate, then poll every 5 s. The
//                  cashier also gets "I've approved" (immediate check) and
//                  "Resend prompt" (re-push on the SAME order id).
//   3. `paid`    — hand the amount back to the parent, which runs the
//                  normal charge-marking / day_income flow with
//                  paymentMethod = 'mobile_money'.
//   4. `failed` / `timed_out` — reason plus a way to retry.
//
// Two things this deliberately does NOT do:
//
//   • It never credits the bill. That stays with the parent (selectedCharges,
//     selectedTotal, PaymentProcess). This component's only job is
//     "money moved / didn't move".
//
//   • It never treats a slow answer as a failure. Zenith's confirmation
//     lags MTN's debit SMS by 5–30 s, so a poll that comes back "pending"
//     just keeps waiting; only an explicit terminal status from the bank,
//     or the cashier cancelling, ends the attempt. Running out of poll
//     budget shows `timed_out` — which says "no answer yet", not "failed"
//     — and the order id stays on screen so it can be looked up later.
//
// Polling budget: 36 attempts × 5 s = 180 s.
// ─────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton,
  FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Chip, LinearProgress, Stack, Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  Replay as ReplayIcon,
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';

import {
  zenithMomoConfig, zenithMomoInitiate, zenithMomoPoll,
  zenithMomoConfirm, zenithMomoResend, zenithMomoCancel,
} from '../BaseURL';

const NETWORKS = [
  { value: 'MTN',         label: 'MTN MoMo' },
  { value: 'VODAFONE',    label: 'Telecel / Vodafone Cash' },
  { value: 'AIRTEL_TIGO', label: 'AirtelTigo Money' },
];

const POLL_INTERVAL_MS  = 5000;
const POLL_MAX_ATTEMPTS = 36; // 36 × 5 s = 180 s

export default function MomoPaymentDialog({
  open,
  onClose,
  amount,
  patient,
  currentUser,
  description,
  onPaid,     // (amount, meta) => void
}) {
  const [phase, setPhase]         = useState('form');   // form | waiting | paid | failed | timed_out
  const [network, setNetwork]     = useState('MTN');
  const [phone, setPhone]         = useState('');
  const [error, setError]         = useState('');
  const [orderID, setOrderID]     = useState(null);
  const [refID, setRefID]         = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [attempts, setAttempts]   = useState(0);
  const [meta, setMeta]           = useState(null);
  const [busy, setBusy]           = useState('');       // '' | 'starting' | 'confirming' | 'resending'
  const [gateway, setGateway]     = useState({ loaded: false, enabled: true, mock: false, configured: true });
  const timerRef = useRef(null);

  // Is the gateway actually usable, and is it in test mode? Asked once per
  // open so the cashier is told up-front rather than after typing a number.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(zenithMomoConfig, { timeout: 10000 });
        if (!alive) return;
        setGateway({
          loaded: true,
          enabled:    !!data?.enabled,
          mock:       !!data?.mock,
          configured: !!data?.configured,
        });
      } catch (_) {
        if (alive) setGateway({ loaded: true, enabled: false, mock: false, configured: false });
      }
    })();
    return () => { alive = false; };
  }, [open]);

  // Prefill the phone from the patient record so the cashier doesn't retype it.
  useEffect(() => {
    if (open && patient?.tel && !phone) {
      const digits = String(patient.tel).replace(/\D/g, '');
      if (digits.length >= 9) setPhone(digits);
    }
  }, [open, patient?.tel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on close so the next open starts clean — no stale polling, no
  // leftover phone number from the previous patient.
  useEffect(() => {
    if (!open) {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      setPhase('form');
      setError('');
      setOrderID(null);
      setRefID(null);
      setStatusMsg('');
      setAttempts(0);
      setMeta(null);
      setBusy('');
    }
  }, [open]);

  // Apply a status payload from /poll or /confirm. Kept in one place so
  // both the timer and the "I've approved" button interpret the gateway
  // identically.
  const applyStatus = useCallback((data) => {
    if (data?.paid) {
      setPhase('paid');
      setStatusMsg(data.message || 'Payment received.');
      setMeta({
        orderID,
        referenceID:   data.referenceID || refID,
        paymentMethod: data.paymentMethod || null,
        customerAcct:  data.customerAcct  || null,
        paymentDate:   data.paymentDate   || null,
        sms:           data.sms           || null,
      });
      return true;
    }
    if (data?.status === 'failed' || data?.status === 'cancelled') {
      setPhase('failed');
      setStatusMsg(data.message || 'The bank reported this payment as unsuccessful.');
      return true;
    }
    if (data?.message) setStatusMsg(data.message);
    return false;
  }, [orderID, refID]);

  // Poll loop — chained setTimeout rather than setInterval so a slow
  // gateway can't stack overlapping requests.
  useEffect(() => {
    if (phase !== 'waiting' || !orderID) return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.post(zenithMomoPoll, { orderID }, { timeout: 30000 });
        if (cancelled) return;
        if (applyStatus(data)) return;
      } catch (err) {
        // A failed poll is a network problem, not a failed payment. Log
        // and keep waiting.
        console.error('[MomoPaymentDialog] poll error:', err?.message);
      }

      setAttempts((n) => {
        const next = n + 1;
        if (next >= POLL_MAX_ATTEMPTS) {
          setPhase('timed_out');
          return next;
        }
        timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        return next;
      });
    };

    // First poll after one interval — ConfirmTransaction lags right after
    // AuthenticateMOMO returns, so an immediate check just wastes a call.
    timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [phase, orderID, applyStatus]);

  // Notify the parent once we've rendered the success state.
  useEffect(() => {
    if (phase === 'paid' && meta && typeof onPaid === 'function') {
      onPaid(Number(amount), meta);
    }
  }, [phase, meta, amount, onPaid]);

  const start = async () => {
    setError('');
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 9) { setError('Enter a valid mobile-money number, e.g. 0557719399.'); return; }
    if (!Number(amount) || Number(amount) <= 0) { setError('The amount must be greater than zero.'); return; }

    setBusy('starting');
    setStatusMsg('Sending the payment prompt to the customer\'s phone…');
    try {
      const { data } = await axios.post(zenithMomoInitiate, {
        amount:      Number(amount),
        phone:       digits,
        network,
        patientId:   patient?.id || null,
        patientName: patient?.fullName || null,
        description: description || `Hospital bill for ${patient?.fullName || 'patient'}`,
        currentUser: currentUser || null,
      }, { timeout: 60000 });

      if (data?.success && data.orderID) {
        setOrderID(data.orderID);
        setRefID(data.referenceID || null);
        setAttempts(0);
        setPhase('waiting');
        setStatusMsg(data.message || 'Payment prompt sent to the customer\'s phone.');
      } else {
        setPhase('failed');
        setStatusMsg(data?.message || 'The payment gateway rejected the request.');
      }
    } catch (err) {
      setPhase('failed');
      setStatusMsg(err.response?.data?.message || err.message || 'Could not reach the payment service.');
    } finally {
      setBusy('');
    }
  };

  // "The customer says they approved" — an immediate check instead of
  // waiting out the poll interval. A non-PAID answer keeps us waiting.
  const confirmNow = async () => {
    if (!orderID) return;
    setBusy('confirming');
    try {
      const { data } = await axios.post(zenithMomoConfirm, { orderID }, { timeout: 40000 });
      applyStatus(data);
    } catch (err) {
      setStatusMsg(err.response?.data?.message || 'Could not check the payment just now — still waiting.');
    } finally {
      setBusy('');
    }
  };

  // Re-push the prompt on the same order. A new order would risk two debits.
  const resendPrompt = async () => {
    if (!orderID) return;
    setBusy('resending');
    try {
      const { data } = await axios.post(zenithMomoResend, { orderID }, { timeout: 40000 });
      setStatusMsg(data?.message || 'Prompt resent to the customer\'s phone.');
      if (phase === 'timed_out') { setAttempts(0); setPhase('waiting'); }
    } catch (err) {
      setStatusMsg(err.response?.data?.message || 'Could not resend the prompt.');
    } finally {
      setBusy('');
    }
  };

  // Abandon the attempt. The row stays in momo_payments so a late
  // settlement is still traceable by order id.
  const cancelAttempt = async () => {
    if (orderID) {
      try { await axios.post(zenithMomoCancel, { orderID }, { timeout: 15000 }); }
      catch (_) { /* the cashier is leaving either way */ }
    }
    onClose();
  };

  const retry = () => {
    setPhase('form');
    setStatusMsg('');
    setError('');
    setOrderID(null);
    setRefID(null);
    setAttempts(0);
  };

  const waiting  = phase === 'waiting';
  const canClose = !waiting;                       // no stray outside-click during a live debit
  const elapsed  = attempts * (POLL_INTERVAL_MS / 1000);
  const budget   = POLL_MAX_ATTEMPTS * (POLL_INTERVAL_MS / 1000);
  const blocked  = gateway.loaded && !gateway.enabled;

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <PhoneIcon sx={{ color: '#e5322a' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.02rem' }}>
              Mobile Money (Zenith)
            </Typography>
            {gateway.mock && (
              <Chip
                size="small"
                label="TEST MODE"
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: '#FEF3C7', color: '#B45309' }}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Amount: <strong>GHS {Number(amount || 0).toFixed(2)}</strong>
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={!canClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── PHASE: FORM ────────────────────────────────────────────── */}
        {phase === 'form' && (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {blocked && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
                The Zenith gateway isn't configured yet. An administrator needs to add the
                API key under <b>System Settings → Payment Gateway</b>. Until then, take this
                payment as cash or manual mobile money.
              </Alert>
            )}
            {gateway.mock && !blocked && (
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                <b>Test mode.</b> No prompt reaches the customer's phone and no money moves —
                the payment will be simulated as successful so the flow can be checked end to end.
              </Alert>
            )}

            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 2 }}>
              A debit prompt goes to the customer's phone and an SMS explains what it is.
              They approve with their MoMo PIN; funds settle into the facility's Zenith
              merchant account.
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Network</InputLabel>
              <Select label="Network" value={network} onChange={(e) => setNetwork(e.target.value)}>
                {NETWORKS.map((n) => (
                  <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Mobile Money Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, '').slice(0, 15))}
              placeholder="e.g. 0557719399"
              helperText="The wallet that will be debited — not necessarily the number on file."
              inputProps={{ inputMode: 'numeric' }}
            />

            {patient?.fullName && (
              <Chip size="small" label={`Patient: ${patient.fullName}`} sx={{ mt: 1.5, fontWeight: 600 }} />
            )}
          </>
        )}

        {/* ── PHASE: WAITING ────────────────────────────────────────── */}
        {waiting && (
          <Box sx={{ textAlign: 'center', py: 1.5 }}>
            <HourglassIcon sx={{ fontSize: 42, color: '#f59e0b', mb: 1 }} />
            <Typography sx={{ fontWeight: 700 }}>Waiting for approval</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              {statusMsg}
            </Typography>

            <LinearProgress
              variant="determinate"
              value={(attempts / POLL_MAX_ATTEMPTS) * 100}
              sx={{ my: 1.5, borderRadius: 2, height: 6 }}
            />
            <Typography variant="caption" color="text.secondary">
              {orderID ? `Order ${orderID} · ${elapsed}s / ${budget}s` : 'Setting up…'}
            </Typography>

            <Alert severity="info" sx={{ mt: 2, textAlign: 'left', fontSize: '0.8rem' }}>
              Tell the customer to check their phone and enter their MoMo PIN. Leave this
              open — we're checking with the bank every {POLL_INTERVAL_MS / 1000} seconds.
            </Alert>

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={busy === 'confirming' ? <CircularProgress size={14} /> : <ThumbUpIcon fontSize="small" />}
                onClick={confirmNow}
                disabled={!!busy}
              >
                They approved
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={busy === 'resending' ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
                onClick={resendPrompt}
                disabled={!!busy}
              >
                Resend prompt
              </Button>
            </Stack>
          </Box>
        )}

        {/* ── PHASE: PAID ───────────────────────────────────────────── */}
        {phase === 'paid' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckIcon sx={{ fontSize: 54, color: '#16a34a', mb: 1 }} />
            <Typography sx={{ fontWeight: 800, color: '#16a34a', fontSize: '1.05rem' }}>
              Payment received
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              GHS {Number(amount || 0).toFixed(2)} paid via {meta?.paymentMethod || 'mobile money'}.
            </Typography>
            {meta?.customerAcct && (
              <Chip size="small" label={`From ${meta.customerAcct}`} sx={{ mt: 1.5, fontWeight: 600 }} />
            )}
            {meta?.sms === 'sent' && (
              <Chip
                size="small"
                icon={<SmsIcon sx={{ fontSize: '13px !important' }} />}
                label="Receipt SMS sent"
                sx={{ mt: 1.5, ml: 0.75, fontWeight: 600 }}
              />
            )}
            {refID && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Reference: <code>{refID}</code>
              </Typography>
            )}
          </Box>
        )}

        {/* ── PHASE: FAILED / TIMED OUT ─────────────────────────────── */}
        {(phase === 'failed' || phase === 'timed_out') && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <ErrorIcon sx={{ fontSize: 42, color: phase === 'timed_out' ? '#f59e0b' : '#dc2626', mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: phase === 'timed_out' ? '#b45309' : '#dc2626' }}>
              {phase === 'timed_out' ? 'Still no answer from the bank' : 'Payment not completed'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {phase === 'timed_out'
                ? `No confirmation after ${budget / 60} minutes. If the customer's wallet was debited the money is not lost — quote the reference below and check the payment again later.`
                : statusMsg}
            </Typography>
            {/* "Check again" writes its answer here, so a still-pending
                retry gives feedback instead of looking like a dead button. */}
            {phase === 'timed_out' && statusMsg && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                {statusMsg}
              </Typography>
            )}
            {(refID || orderID) && (
              <Box sx={{ mt: 1.5 }}>
                {refID && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Reference: <code>{refID}</code>
                  </Typography>
                )}
                {orderID && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Order: <code>{orderID}</code>
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25 }}>
        {phase === 'form' && (
          <>
            <Button onClick={onClose} color="inherit">Cancel</Button>
            <Button
              variant="contained"
              onClick={start}
              disabled={!phone || !network || blocked || busy === 'starting'}
              startIcon={busy === 'starting' ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PhoneIcon />}
            >
              {busy === 'starting' ? 'Sending…' : 'Send prompt'}
            </Button>
          </>
        )}
        {waiting && (
          <Button onClick={cancelAttempt} color="inherit" disabled={!!busy}>
            Cancel payment
          </Button>
        )}
        {phase === 'paid' && (
          <Button variant="contained" color="success" onClick={onClose}>Done</Button>
        )}
        {(phase === 'failed' || phase === 'timed_out') && (
          <>
            <Button onClick={onClose} color="inherit">Close</Button>
            {phase === 'timed_out' && (
              <Button onClick={confirmNow} disabled={!!busy} startIcon={<ThumbUpIcon />}>
                Check again
              </Button>
            )}
            <Button variant="contained" onClick={retry} startIcon={<ReplayIcon />}>
              Try again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
